import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./site.css";

import Hero from "./sections/Hero";
import Arc from "./sections/Arc";
import Channels from "./sections/Channels";
import Gap from "./sections/Gap";
import Console from "./sections/Console";
import Facts from "./sections/Facts";
import Contact from "./sections/Contact";
import { useActiveSection, useReveal, useStuck } from "./hooks/useScroll";

const LINKS = [
  { id: "arc", t: "How it runs" },
  { id: "channels", t: "Where" },
  { id: "control", t: "Control" },
  { id: "truth", t: "Straight answers" },
  { id: "contact", t: "Contact" },
];

/* Dark is the default and it does not follow the operating system. Somebody
   arriving from a printed code at night should get the dark one, and somebody
   who chooses light should keep it. */
function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return window.localStorage.getItem("sns-theme") === "light" ? "light" : "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.classList.add("theming");
    const t = setTimeout(() => root.classList.remove("theming"), 460);
    try {
      window.localStorage.setItem("sns-theme", theme);
    } catch (e) {
      /* private mode, not worth breaking the page over */
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "light" ? "#ffffff" : "#08090b");
    return () => clearTimeout(t);
  }, [theme]);

  return [theme, () => setTheme((v) => (v === "dark" ? "light" : "dark"))];
}

const Sun = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4 17 7M7 17l-1.6 1.6" />
  </svg>
);
const Moon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.5 14.4A8.6 8.6 0 0 1 9.6 3.5a8.6 8.6 0 1 0 10.9 10.9z" />
  </svg>
);

export default function App() {
  const [theme, toggleTheme] = useTheme();
  const stuck = useStuck();
  const [menu, setMenu] = useState(false);
  const ids = useMemo(() => LINKS.map((l) => l.id), []);
  const active = useActiveSection(ids);
  const footRef = useReveal();

  const go = useCallback((e, id) => {
    e.preventDefault();
    setMenu(false);
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 8;
    window.scrollTo({ top: y, behavior: "smooth" });
  }, []);

  /* Only now does the stylesheet get permission to hide anything. See the
     reveals block in site.css. */
  useEffect(() => {
    document.documentElement.classList.add("rv-on");
  }, []);

  useEffect(() => {
    document.body.style.overflow = menu ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menu]);

  const mark = theme === "light" ? "/sns-mark-dark.png" : "/sns-mark-white.png";
  const year = new Date().getFullYear();

  return (
    <>
      <header className={`nav${stuck ? " is-stuck" : ""}`}>
        <div className="nav__in">
          <a href="#home" onClick={(e) => go(e, "home")} aria-label="SipNScore, home">
            <img className="nav__mark" src={mark} alt="SipNScore" width="480" height="156" />
          </a>

          <nav className="nav__links">
            {LINKS.map((l) => (
              <a
                key={l.id}
                className={`nav__link${active === l.id ? " is-on" : ""}`}
                href={`#${l.id}`}
                onClick={(e) => go(e, l.id)}
              >
                {l.t}
              </a>
            ))}
          </nav>

          <div className="nav__act">
            <button
              className="toggle"
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light" : "Switch to dark"}
            >
              {theme === "dark" ? <Sun /> : <Moon />}
            </button>
            <a className="btn btn--sm" href="/demo/">
              Try the demo
            </a>
            <button
              className="burger"
              type="button"
              onClick={() => setMenu((v) => !v)}
              aria-label={menu ? "Close menu" : "Open menu"}
              aria-expanded={menu}
            >
              {menu ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
                  strokeLinecap="round"><path d="M5 5l14 14M19 5 5 19" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
                  strokeLinecap="round"><path d="M3.5 7.5h17M3.5 16.5h17" /></svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {menu && (
        <div className="sheet">
          {LINKS.map((l) => (
            <a key={l.id} href={`#${l.id}`} onClick={(e) => go(e, l.id)}>
              {l.t}
            </a>
          ))}
          <a className="btn" href="/demo/">
            Be the guest for 60 seconds
          </a>
        </div>
      )}

      <main>
        <Hero />
        <Arc />
        <Gap />
        <Channels />
        <Console />
        <Facts />
        <Contact theme={theme} />
      </main>

      <footer className="foot" ref={footRef}>
        <div className="shell">
          <div className="foot__in">
            <div className="rv">
              <img className="foot__mark" src={mark} alt="SipNScore" width="480" height="156" />
              <p className="body" style={{ marginTop: 16, maxWidth: "34ch", fontSize: 14 }}>
                Scan to insight, for any brand in any trade channel. Built and
                hosted in South Africa.
              </p>
            </div>
            <div className="rv" style={{ "--d": "70ms" }}>
              <h5>The product</h5>
              <ul>
                <li><a href="#arc">How it runs</a></li>
                <li><a href="#channels">Trade channels</a></li>
                <li><a href="#control">SipNScore Control</a></li>
                <li><a href="/demo/">The 60 second demo</a></li>
              </ul>
            </div>
            <div className="rv" style={{ "--d": "140ms" }}>
              <h5>Us</h5>
              <ul>
                <li><a href="#truth">Straight answers</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><a href="mailto:info@sipnscore.co.za">info@sipnscore.co.za</a></li>
              </ul>
            </div>
            <div className="rv" style={{ "--d": "210ms" }}>
              <h5>Legal</h5>
              <ul>
                <li><a href="/pricing/">Pricing</a></li>
                <li><a href="/legal/#terms">Terms of service</a></li>
                <li><a href="/legal/#privacy">Privacy + POPIA</a></li>
                <li><a href="/legal/#refunds">Refunds + cancellations</a></li>
                <li><a href="/legal/#disputes">Disputes</a></li>
              </ul>
            </div>
          </div>
          <div className="foot__base micro">
            <span>&copy; {year} SipNScore</span>
            <span><a href="/legal/">Legal</a></span>
            <span>On-trade / Off-trade / Online / B2B / Experiential</span>
          </div>
        </div>
      </footer>
    </>
  );
}
